export {
  buildRealtimeVoiceAgentInstructions,
  REALTIME_VOICE_AGENT_INSTRUCTIONS,
  REALTIME_VOICE_AGENT_INSTRUCTIONS_BASE,
} from '@/lib/realtime-voice-instructions';

export type RealtimeVoiceDelegationMode = 'queue' | 'interrupt';

export function normalizeRealtimeDelegationMode(
  mode: unknown
): RealtimeVoiceDelegationMode {
  return mode === 'interrupt' ? 'interrupt' : 'queue';
}

export type RealtimeVoiceDelegationDispatchStatus = 'queued' | 'started';

export type RealtimeVoiceDelegationDispatchResult = {
  status: RealtimeVoiceDelegationDispatchStatus;
  message: string;
};

export type RealtimeVoiceDelegationPresetData = {
  productPresetId: null;
  stylePresetId: null;
  colorPresetId: null;
  forceDeepResearch?: boolean;
};

type RealtimeVoiceDelegationDeliveryResult = {
  status: RealtimeVoiceDelegationDispatchStatus;
  accepted?: boolean;
};

export type RealtimeVoiceDelegationTarget = {
  pending: boolean;
  dispatchMessage?: (
    prompt: string,
    attachments?: undefined,
    presetData?: RealtimeVoiceDelegationPresetData,
    userPrompt?: string,
    options?: { realtimeVoiceSessionId?: string }
  ) =>
    | Promise<RealtimeVoiceDelegationDeliveryResult>
    | RealtimeVoiceDelegationDeliveryResult;
  enqueueMessage?: (
    prompt: string,
    attachments?: undefined,
    presetData?: RealtimeVoiceDelegationPresetData,
    userPrompt?: string,
    queueId?: string,
    options?: { realtimeVoiceSessionId?: string }
  ) => Promise<void> | void;
  sendMessage: (
    prompt: string,
    attachments?: undefined,
    presetData?: RealtimeVoiceDelegationPresetData,
    userPrompt?: string,
    options?: { realtimeVoiceSessionId?: string }
  ) =>
    | Promise<RealtimeVoiceDelegationDeliveryResult | void>
    | RealtimeVoiceDelegationDeliveryResult
    | void;
  sendMessageNow?: (
    prompt: string,
    attachments?: undefined,
    presetData?: RealtimeVoiceDelegationPresetData,
    userPrompt?: string,
    options?: { realtimeVoiceSessionId?: string }
  ) =>
    | Promise<RealtimeVoiceDelegationDeliveryResult>
    | RealtimeVoiceDelegationDeliveryResult;
  stopStreaming?: (options?: {
    realtimeVoiceSessionId?: string;
  }) => Promise<boolean> | boolean;
};

const REALTIME_VOICE_DEEP_RESEARCH_INTENT_SIGNALS = [
  'deep research',
  'deep dive',
  'thorough research',
  'thorough analysis',
  'comprehensive research',
  'comprehensive analysis',
  'in depth research',
  'source backed report',
  'launch strategy',
  'competitive analysis',
  'go to market',
  'market opportunity',
];

function normalizeRealtimeVoiceResearchPrompt(prompt: string): string {
  return prompt
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isRealtimeVoiceDeepResearchIntentPrompt(
  prompt: string
): boolean {
  const normalized = normalizeRealtimeVoiceResearchPrompt(prompt);
  if (!normalized) return false;
  return REALTIME_VOICE_DEEP_RESEARCH_INTENT_SIGNALS.some((signal) =>
    normalized.includes(signal)
  );
}

function buildRealtimeVoiceDelegationPresetData(
  prompt: string
): RealtimeVoiceDelegationPresetData | undefined {
  return isRealtimeVoiceDeepResearchIntentPrompt(prompt)
    ? {
        productPresetId: null,
        stylePresetId: null,
        colorPresetId: null,
        forceDeepResearch: true,
      }
    : undefined;
}

function getRealtimeVoiceDelegationDispatchMessage({
  status,
  mode,
}: {
  status: RealtimeVoiceDelegationDispatchStatus;
  mode: RealtimeVoiceDelegationMode;
}): string {
  const pendingNotice =
    'This receipt confirms acceptance in the original voice chat only. The requested work, including any message to another chat, is still pending. Tell the user the request was handed off; confirm destination delivery only after a result from the General Agent verifies it.';
  if (status === 'queued') {
    return `The request was queued behind the current workspace task. ${pendingNotice}`;
  }
  return mode === 'interrupt'
    ? `The current task was asked to stop and the replacement request has started in the original voice chat. ${pendingNotice}`
    : `The request has started in the original voice chat. ${pendingNotice}`;
}

export function shouldQueueRealtimeVoiceDelegation({
  pending,
  hasQueueHandler,
}: {
  pending: boolean;
  hasQueueHandler: boolean;
}): boolean {
  return pending && hasQueueHandler;
}

export async function dispatchRealtimeVoiceDelegationToOrgChat({
  target,
  message,
  mode,
  realtimeVoiceSessionId,
}: {
  target: RealtimeVoiceDelegationTarget;
  message: string;
  mode: RealtimeVoiceDelegationMode;
  realtimeVoiceSessionId?: string;
}): Promise<RealtimeVoiceDelegationDispatchResult> {
  const prompt = message.trim();
  if (!prompt) {
    throw new Error('Delegated message is empty');
  }
  const presetData = buildRealtimeVoiceDelegationPresetData(prompt);
  const options = realtimeVoiceSessionId
    ? { realtimeVoiceSessionId }
    : undefined;

  if (mode === 'interrupt') {
    const stopped = await target.stopStreaming?.(options);
    if (stopped && target.sendMessageNow) {
      const result = options
        ? await target.sendMessageNow(
            prompt,
            undefined,
            presetData,
            prompt,
            options
          )
        : await target.sendMessageNow(prompt, undefined, presetData, prompt);
      if (result.accepted === false) {
        throw new Error('Failed to send message. Please try again.');
      }
      return {
        status: result.status,
        message: getRealtimeVoiceDelegationDispatchMessage({
          status: result.status,
          mode,
        }),
      };
    }
  }

  if (target.dispatchMessage) {
    const result = options
      ? await target.dispatchMessage(
          prompt,
          undefined,
          presetData,
          prompt,
          options
        )
      : await target.dispatchMessage(prompt, undefined, presetData, prompt);
    if (result.accepted === false) {
      throw new Error('Failed to send message. Please try again.');
    }
    return {
      status: result.status,
      message: getRealtimeVoiceDelegationDispatchMessage({
        status: result.status,
        mode,
      }),
    };
  }

  if (
    shouldQueueRealtimeVoiceDelegation({
      pending: target.pending,
      hasQueueHandler: Boolean(target.enqueueMessage),
    })
  ) {
    if (options) {
      await target.enqueueMessage?.(
        prompt,
        undefined,
        presetData,
        prompt,
        undefined,
        options
      );
    } else {
      await target.enqueueMessage?.(prompt, undefined, presetData, prompt);
    }
    return {
      status: 'queued',
      message: getRealtimeVoiceDelegationDispatchMessage({
        status: 'queued',
        mode,
      }),
    };
  }

  const receipt = options
    ? await target.sendMessage(prompt, undefined, presetData, prompt, options)
    : await target.sendMessage(prompt, undefined, presetData, prompt);
  if (receipt?.accepted === false)
    throw new Error('Message send was not accepted');
  return {
    status: 'started',
    message: getRealtimeVoiceDelegationDispatchMessage({
      status: 'started',
      mode,
    }),
  };
}

export function buildRealtimeStatusPrompt(text: string): string {
  const cleaned = text.trim().replace(/\s+/g, ' ').slice(0, 1200);
  return `[SYSTEM STATUS UPDATE]\n${cleaned}`;
}

export type ExpectedRealtimeResponse =
  | {
      kind: 'user_speech';
      speechTurnId: number;
      reason: string;
    }
  | {
      kind: 'internal_status';
      eventId: string;
      reason: string;
    }
  | {
      kind: 'deferred_tool_continuation';
      speechTurnId: number;
      toolName: string;
      reason: string;
    }
  | {
      kind: 'questionnaire_prompt';
      promptKey: string;
      reason: string;
    }
  | {
      kind: 'opening_greeting';
      sessionId: string;
      reason: string;
    };

export type QueuedRealtimeInternalMessage = {
  /** Called after the client transport accepts application facts and speech request. */
  onDelivered?: () => void;
  validate?: () => Promise<boolean>;
  requiresChat?: boolean;
  onDiscarded?: () => void;
  /** Retry admission after transient validation failures, without acknowledging delivery. */
  onDeferred?: () => void;
  key: string;
  message: string;
  ticket: Extract<
    ExpectedRealtimeResponse,
    { kind: 'internal_status' | 'questionnaire_prompt' }
  >;
};

export type QueuedRealtimeToolContinuation = Extract<
  ExpectedRealtimeResponse,
  { kind: 'deferred_tool_continuation' }
>;

export function appendExpectedRealtimeResponse(
  queue: Array<ExpectedRealtimeResponse>,
  ticket: ExpectedRealtimeResponse
): Array<ExpectedRealtimeResponse> {
  return [...queue, ticket];
}

export function consumeExpectedRealtimeResponse(
  queue: Array<ExpectedRealtimeResponse>
): {
  ticket: ExpectedRealtimeResponse | null;
  queue: Array<ExpectedRealtimeResponse>;
} {
  const [ticket, ...remaining] = queue;
  return {
    ticket: ticket ?? null,
    queue: remaining,
  };
}

export function upsertQueuedRealtimeInternalMessage(
  queue: Array<QueuedRealtimeInternalMessage>,
  next: QueuedRealtimeInternalMessage
): Array<QueuedRealtimeInternalMessage> {
  return [...queue.filter((item) => item.key !== next.key), next];
}

export function dequeueQueuedRealtimeInternalMessage(
  queue: Array<QueuedRealtimeInternalMessage>
): {
  item: QueuedRealtimeInternalMessage | null;
  queue: Array<QueuedRealtimeInternalMessage>;
} {
  const [item, ...remaining] = queue;
  return {
    item: item ?? null,
    queue: remaining,
  };
}

export function upsertQueuedRealtimeToolContinuation(
  queue: Array<QueuedRealtimeToolContinuation>,
  next: QueuedRealtimeToolContinuation
): Array<QueuedRealtimeToolContinuation> {
  return [
    ...queue.filter(
      (item) =>
        item.speechTurnId !== next.speechTurnId ||
        item.toolName !== next.toolName
    ),
    next,
  ];
}

export function dequeueQueuedRealtimeToolContinuation(
  queue: Array<QueuedRealtimeToolContinuation>
): {
  item: QueuedRealtimeToolContinuation | null;
  queue: Array<QueuedRealtimeToolContinuation>;
} {
  const [item, ...remaining] = queue;
  return {
    item: item ?? null,
    queue: remaining,
  };
}

export function shouldDeferRealtimeToolResult(args: {
  assistantSpeaking: boolean;
}): boolean {
  return args.assistantSpeaking;
}

export function shouldQueueRealtimeInternalMessage(args: {
  assistantSpeaking: boolean;
  activeRealtimeResponse: boolean;
  expectedResponses: Array<ExpectedRealtimeResponse>;
}): boolean {
  if (args.assistantSpeaking || args.activeRealtimeResponse) return true;
  return args.expectedResponses.some(
    (ticket) =>
      ticket.kind === 'user_speech' ||
      ticket.kind === 'deferred_tool_continuation'
  );
}

export function shouldInjectRealtimeStatusEvent(
  eventId: string | null | undefined,
  lastSeenEventId: string | null | undefined,
  eventSessionId?: string | null,
  currentSessionId?: string | null
): eventId is string {
  if (eventSessionId && eventSessionId !== currentSessionId) {
    return false;
  }
  return Boolean(eventId && eventId !== lastSeenEventId);
}

export function shouldAllowRealtimeVoiceDelegationForSpeechTurn({
  currentSpeechTurnId,
  lastDelegatedSpeechTurnId,
}: {
  currentSpeechTurnId: number;
  lastDelegatedSpeechTurnId: number;
}): boolean {
  return (
    currentSpeechTurnId > 0 && currentSpeechTurnId > lastDelegatedSpeechTurnId
  );
}

export const REALTIME_VOICE_STARTUP_SPEECH_GRACE_MS = 700;

export function shouldCountRealtimeSpeechStartedAsUserTurn({
  now,
  unmutedAt,
  pttActive,
}: {
  now: number;
  unmutedAt: number | null | undefined;
  pttActive: boolean;
}): boolean {
  if (pttActive) return true;
  if (unmutedAt == null) return false;
  return now - unmutedAt >= REALTIME_VOICE_STARTUP_SPEECH_GRACE_MS;
}
